/* eslint-disable react/no-unstable-nested-components */
import React from 'react';
import ReactModal from 'react-modal';

import { ZIndex } from '@rover/constants/src/styles/ZIndex';
import { Box, Flex } from '@rover/kibble/core';
import Elevator from '@rover/react-lib/src/components/utils/Elevator';

import Content from './Content/Content';
import { ModalCommonProps, usePreventScroll, useReactModalSetup } from './Modal.common';
import Overlay from './Overlay';

/** Props for web Modal component */
export type Props = ModalCommonProps & {
  /** ARIA attributes object */
  aria?: Record<string, string>;
  /** Selector for app root element for ReactModal */
  appElementSelector?: string;
  /** ID for header element for aria-labelledby */
  headerId?: string;
  /** Fixed height for content wrapper */
  contentWrapperHeight?: string;
  /** Ref callback for modal element */
  modalRef?: (instance: HTMLElement) => void;
};

/**
 * Modal displays content in a centered overlay dialog.
 * Includes header, action buttons, and accessibility features.
 *
 * READ THE DOCS: https://roverdotcom.atlassian.net/wiki/spaces/TECH/pages/4106125472/Modal
 */
export default function Modal({
  children,
  isOpen,
  onRequestClose,
  headerText,
  headerId,
  headerSize,
  showCloseIcon,
  closeIconSize,
  appElementSelector = '#base-content',
  contentProps = {},
  headerProps,
  contentWrapperHeight,
  overlaySx = {},
  modalRef,
  ...other
}: Props): JSX.Element {
  const { staticHeaderId, modalId } = useReactModalSetup(appElementSelector);
  const actualHeaderId = headerId || staticHeaderId;
  usePreventScroll(isOpen, modalId);

  // ReactModal takes all aria-* props in an `aria` object.
  const ariaProps = other.aria || {};
  Object.keys(other).forEach((key) => {
    if (key.startsWith('aria-')) {
      ariaProps[key.substring(5)] = other[key];
      delete other[key]; // eslint-disable-line no-param-reassign
    }
  });

  return (
    <ReactModal
      aria={{ labelledby: actualHeaderId }}
      contentRef={modalRef}
      className="RoverModalContent"
      closeTimeoutMS={150}
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      overlayClassName="RoverModalOverlay"
      overlayElement={(props, overlayChildren): JSX.Element => (
        <Overlay data-testid="modal-overlay" {...props} sx={overlaySx}>
          {overlayChildren}
        </Overlay>
      )}
    >
      <Box {...ariaProps}>
        <Elevator elevation={4}>
          <Flex
            background="primary"
            borderRadius="primary"
            height="auto"
            maxWidth="600px"
            minHeight="100px"
            maxHeight="100vh"
            padding="6x"
            width="auto"
            zIndex={ZIndex.MODAL.toString()}
            {...contentProps}
          >
            <Content
              headerId={actualHeaderId}
              headerText={headerText}
              headerSize={headerSize}
              onRequestClose={onRequestClose}
              showCloseIcon={showCloseIcon}
              closeIconSize={closeIconSize}
              headerProps={headerProps}
              contentWrapperHeight={contentWrapperHeight}
              {...other}
            >
              {children}
            </Content>
          </Flex>
        </Elevator>
      </Box>
    </ReactModal>
  );
}
